import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, Search, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { ThemeSelector } from '../ui/ThemeSelector';

export const TopNavBar = ({ title, showBack = true, showHome = true, rightContent }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  // Don't show back button on main pages
  const mainPages = ['/', '/dashboard', '/login', '/register'];
  const isMainPage = mainPages.includes(location.pathname);
  const isSearchPage = location.pathname === '/search';
  
  const handleBack = () => {
    navigate(-1);
  };
  
  const handleHome = () => {
    navigate('/dashboard');
  };

  const handleSearchClick = () => {
    navigate('/search');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-30 border-b transition-colors duration-300"
      style={{
        backgroundColor: isDark ? 'rgba(9, 8, 14, 0.8)' : 'rgba(250, 250, 250, 0.8)',
        borderColor: 'var(--border-color)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}
    >
      <div className="flex items-center justify-between px-4 py-3">
        {/* Left side - Back & Home buttons */}
        <div className="flex items-center gap-2">
          {showBack && !isMainPage && (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--bg-card)] hover:bg-[var(--bg-card-hover)] border border-[var(--border-color)] hover:border-[var(--border-color-hover)] transition-all group"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)] transition-colors" />
              <span className="text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)] text-sm font-medium transition-colors hidden sm:inline">Back</span>
            </button>
          )}
          
          {showHome && !isMainPage && (
            <button
              onClick={handleHome}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--bg-card)] hover:bg-[var(--bg-card-hover)] border border-[var(--border-color)] hover:border-[var(--border-color-hover)] transition-all group"
              data-testid="home-btn"
            >
              <Home className="w-4 h-4 text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)] transition-colors" />
              <span className="text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)] text-sm font-medium transition-colors hidden sm:inline">Home</span>
            </button>
          )}
          
          {title && (
            <h1 className="text-[var(--text-primary)] font-medium ml-2 hidden md:block">{title}</h1>
          )}
        </div>

        {/* Center - AIRA Search Bar (Only show if not on search page) */}
        {!isSearchPage && (
          <motion.button
            onClick={handleSearchClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="hidden md:flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 hover:border-purple-500/40 hover:from-purple-500/20 hover:to-cyan-500/20 transition-all duration-300 cursor-pointer group min-w-[280px]"
            data-testid="aira-search-shortcut"
          >
            <Search className="w-4 h-4 text-purple-400 group-hover:text-purple-300" />
            <span className="text-[var(--text-tertiary)] text-sm group-hover:text-[var(--text-secondary)]">Search anything with AIRA...</span>
            <div className="flex items-center gap-1 ml-auto">
              <Sparkles className="w-3 h-3 text-cyan-400" />
              <span className="text-xs text-cyan-400/70 font-medium">AI</span>
            </div>
          </motion.button>
        )}

        {/* Right side - Theme toggle + Custom content */}
        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <ThemeSelector variant="icon" />
          
          {/* Custom content */}
          {rightContent && rightContent}
        </div>
      </div>
    </motion.div>
  );
};

export default TopNavBar;
